const isBrowser = () => typeof window !== "undefined";

let passiveSupported = null;

const passiveEventsSupported = () => {
	if (passiveSupported !== null) return passiveSupported;
	
	passiveSupported = false;
	try {
		const options = {
			get passive() {
				passiveSupported = true;
			}
		};
		window.addEventListener("test", options, options);
		window.removeEventListener("test", options, options);
	} catch (err) {
		passiveSupported = false;
	}
	
	return passiveSupported;
};

const isSupported = () => !!(isBrowser() && requestAnimationFrame);

const isDocument = (obj) => obj.nodeType === 9;
const isWindow = (obj) => obj && obj.document && isDocument(obj.document);

const windowScroller = (win) => {
	const doc = win.document;
	const body = doc.body;
	const html = doc.documentElement;

	return {
		scrollHeight: () => Math.max(
			body.scrollHeight,
			html.scrollHeight,
			body.offsetHeight,
			html.offsetHeight,
			body.clientHeight,
			html.clientHeight
		),

		height: () => win.innerHeight || html.clientHeight || body.clientHeight,

		scrollY: () => {
			if (win.pageYOffset !== undefined) {
				return win.pageYOffset;
			}
			return (html || body.parentNode || body).scrollTop;
		}
	};
};

const elementScroller = (element) => ({
	scrollHeight: () => Math.max(
		element.scrollHeight,
		element.offsetHeight,
		element.clientHeight
	),

	height: () => Math.max(element.offsetHeight, element.clientHeight),

	scrollY: () => element.scrollTop
});

const createScroller = (element) => 
	isWindow(element) ? windowScroller(element) : elementScroller(element);

const trackScroll = (element, options, callback) => {
	const isPassiveSupported = passiveEventsSupported();
	let rafId;
	let scrolled = false;
	const scroller = createScroller(element);
	let lastScrollY = scroller.scrollY();
	const details = {};

	const update = () => {
		const scrollY = Math.round(scroller.scrollY());
		const height = scroller.height();
		const scrollHeight = scroller.scrollHeight();

		details.scrollY = scrollY;
		details.lastScrollY = lastScrollY;
		details.direction = scrollY > lastScrollY ? "down" : "up";
		details.distance = Math.abs(scrollY - lastScrollY);
		// FIX: Add tolerance of 1 pixel to prevent false out-of-bounds at bottom
		details.isOutOfBounds = scrollY < 0 || scrollY + height > scrollHeight + 1;
		details.top = scrollY <= options.offset[details.direction];
		details.bottom = scrollY + height >= scrollHeight;
		details.toleranceExceeded = details.distance > options.tolerance[details.direction];

		callback(details);

		lastScrollY = scrollY;
		scrolled = false;
	};

	const handleScroll = () => {
		if (!scrolled) {
			scrolled = true;
			rafId = requestAnimationFrame(update);
		}
	};

	const eventOptions = isPassiveSupported
		? { passive: true, capture: false }
		: false;

	element.addEventListener("scroll", handleScroll, eventOptions);
	update();

	return {
		destroy: () => {
			cancelAnimationFrame(rafId);
			element.removeEventListener("scroll", handleScroll, eventOptions);
		}
	};
};

const normalizeUpDown = (t) => (t === Object(t) ? t : { down: t, up: t });

/**
 * Headroom - Hide your header until you need it
 */
export class Headroom {
	static options = {
		tolerance: { up: 0, down: 0 },
		offset: 0,
		scroller: isBrowser() ? window : null,
		classes: {
			frozen: "headroom--frozen",
			pinned: "headroom--pinned",
			unpinned: "headroom--unpinned",
			top: "headroom--top",
			notTop: "headroom--not-top",
			bottom: "headroom--bottom",
			notBottom: "headroom--not-bottom",
			initial: "headroom"
		}
	};

	static cutsTheMustard = isSupported();

	/**
	 * @param {HTMLElement} elem - The header element
	 * @param {Object} options - Configuration options
	 */
	constructor(elem, options = {}) {
		if (!elem) {
			throw new Error("Headroom: Header element is required");
		}

		Object.assign(this, Headroom.options, options);
		this.classes = Object.assign({}, Headroom.options.classes, options.classes);

		this.elem = elem;
		this.tolerance = normalizeUpDown(this.tolerance);
		this.offset = normalizeUpDown(this.offset);
		this.initialised = false;
		this.frozen = false;
	}

	/**
	 * Start listening to scrolling
	 */
	init() {
		if (!Headroom.cutsTheMustard || this.initialised) {
			return this;
		}

		this.addClass("initial");
		this.initialised = true;

		// Defer event registration to handle browser potentially restoring previous scroll position
		setTimeout(() => {
			this.scrollTracker = trackScroll(
				this.scroller,
				{ offset: this.offset, tolerance: this.tolerance },
				this.update.bind(this)
			);
		}, 100);

		return this;
	}

	/**
	 * Destroy the widget, clearing up after itself
	 */
	destroy() {
		this.initialised = false;
		Object.keys(this.classes).forEach(this.removeClass, this);
		if (this.scrollTracker) {
			this.scrollTracker.destroy();
		}
	}

	/**
	 * Unpin the element
	 */
	unpin() {
		if (this.hasClass("pinned") || !this.hasClass("unpinned")) {
			this.addClass("unpinned");
			this.removeClass("pinned");
			this.onUnpin?.call(this);
		}
	}

	/**
	 * Pin the element
	 */
	pin() {
		if (this.hasClass("unpinned")) {
			this.addClass("pinned");
			this.removeClass("unpinned");
			this.onPin?.call(this);
		}
	}

	/**
	 * Freezes the current state of the widget
	 */
	freeze() {
		this.frozen = true;
		this.addClass("frozen");
	}

	/**
	 * Re-enables the default behaviour of the widget
	 */
	unfreeze() {
		this.frozen = false;
		this.removeClass("frozen");
	}

	top() {
		if (!this.hasClass("top")) {
			this.addClass("top");
			this.removeClass("notTop");
			this.onTop?.call(this);
		}
	}

	notTop() {
		if (!this.hasClass("notTop")) {
			this.addClass("notTop");
			this.removeClass("top");
			this.onNotTop?.call(this);
		}
	}

	bottom() {
		if (!this.hasClass("bottom")) {
			this.addClass("bottom");
			this.removeClass("notBottom");
			this.onBottom?.call(this);
		}
	}

	notBottom() {
		if (!this.hasClass("notBottom")) {
			this.addClass("notBottom");
			this.removeClass("bottom");
			this.onNotBottom?.call(this);
		}
	}

	shouldUnpin(details) {
		const scrollingDown = details.direction === "down";
		return scrollingDown && !details.top && details.toleranceExceeded;
	}

	shouldPin(details) {
		const scrollingUp = details.direction === "up";
		return (scrollingUp && details.toleranceExceeded) || details.top;
	}

	addClass(className) {
		if (this.classes[className]) {
			this.elem.classList.add(...this.classes[className].split(" "));
		}
	}

	removeClass(className) {
		if (this.classes[className]) {
			this.elem.classList.remove(...this.classes[className].split(" "));
		}
	}

	hasClass(className) {
		return this.classes[className]
			?.split(" ")
			.every(cls => this.elem.classList.contains(cls)) ?? false;
	}

	update = (details) => {
		if (details.isOutOfBounds || this.frozen) {
			return;
		}

		if (details.top) {
			this.top();
		} else {
			this.notTop();
		}

		if (details.bottom) {
			this.bottom();
		} else {
			this.notBottom();
		}

		if (this.shouldUnpin(details)) {
			this.unpin();
		} else if (this.shouldPin(details)) {
			this.pin();
		}
	};
}

export default Headroom;